import { useState, useEffect, useCallback, useRef } from 'react';
import { SpotPricesData, UseSpotPricesState } from '@/types/spotPrices';
import { getSpotPrices, refreshSpotPrices } from '@/services/spotPrices/spotPricesService';

// Configurazione polling
const POLLING_INTERVAL = 30000; // 30 secondi
const INITIAL_DELAY = 1000; // 1 secondo di delay iniziale

/**
 * Hook per gestire i prezzi spot con aggiornamento automatico
 */
export function useSpotPrices(): UseSpotPricesState {
  const [data, setData] = useState<SpotPricesData>({
    oro: null,
    argento: null,
    isLoading: true,
    error: null,
    lastFetch: null
  });
  
  const [isRefetching, setIsRefetching] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);

  /**
   * Fetch dei prezzi spot
   */
  const fetchPrices = useCallback(async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setIsRefetching(true);
      } else if (data.oro === null && data.argento === null) {
        // Solo per il primo caricamento
        setData(prev => ({ ...prev, isLoading: true, error: null }));
      }

      console.log('[useSpotPrices] 🔄 Fetching spot prices...');
      
      const prices = isManualRefresh 
        ? await refreshSpotPrices()
        : await getSpotPrices();

      if (!mountedRef.current) return;

      setData(prev => ({
        oro: prices.oro,
        argento: prices.argento,
        isLoading: false,
        error: null,
        lastFetch: new Date()
      }));

      console.log('[useSpotPrices] ✅ Spot prices updated:', {
        oro: prices.oro?.price,
        argento: prices.argento?.price
      });

    } catch (error) {
      console.error('[useSpotPrices] ❌ Error fetching spot prices:', error);
      
      if (!mountedRef.current) return;

      setData(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Errore nel caricamento dei prezzi'
      }));
    } finally {
      if (mountedRef.current) {
        setIsRefetching(false);
      }
    }
  }, [data.oro, data.argento]);

  /**
   * Avvia il polling
   */
  const startPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      if (mountedRef.current) {
        fetchPrices(false);
      }
    }, POLLING_INTERVAL);

    console.log('[useSpotPrices] 🔄 Polling started (every 30s)');
  }, [fetchPrices]);

  /**
   * Ferma il polling
   */
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      console.log('[useSpotPrices] ⏹️ Polling stopped');
    }
  }, []);

  /**
   * Refresh manuale
   */
  const refetch = useCallback(async () => {
    await fetchPrices(true);
  }, [fetchPrices]);

  // Effect per il caricamento iniziale e setup polling
  useEffect(() => {
    mountedRef.current = true;

    // Caricamento iniziale con delay
    const initialTimeout = setTimeout(() => {
      if (mountedRef.current) {
        fetchPrices(false);
      }
    }, INITIAL_DELAY);

    // Avvia polling dopo il primo caricamento
    const pollingTimeout = setTimeout(() => {
      if (mountedRef.current) {
        startPolling();
      }
    }, INITIAL_DELAY + 1000);

    return () => {
      mountedRef.current = false;
      clearTimeout(initialTimeout);
      clearTimeout(pollingTimeout);
      stopPolling();
    };
  }, [fetchPrices, startPolling, stopPolling]);

  // Effect per gestire la visibilità della pagina
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Pagina nascosta - ferma il polling
        stopPolling();
        console.log('[useSpotPrices] 👁️ Page hidden - polling paused');
      } else {
        // Pagina visibile - riprendi il polling e aggiorna subito
        console.log('[useSpotPrices] 👁️ Page visible - resuming polling');
        fetchPrices(false);
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchPrices, startPolling, stopPolling]);

  // Effect per gestire focus/blur della finestra
  useEffect(() => {
    const handleFocus = () => {
      if (mountedRef.current) {
        console.log('[useSpotPrices] 🎯 Window focused - refreshing prices');
        fetchPrices(false);
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchPrices]);

  return {
    data,
    refetch,
    isRefetching
  };
}

/**
 * Hook semplificato per ottenere solo i prezzi spot senza polling
 */
export function useSpotPricesOnce() {
  const [data, setData] = useState<SpotPricesData>({
    oro: null,
    argento: null,
    isLoading: true,
    error: null,
    lastFetch: null
  });

  useEffect(() => {
    let mounted = true;

    const fetchOnce = async () => {
      try {
        const prices = await getSpotPrices();
        
        if (mounted) {
          setData({
            oro: prices.oro,
            argento: prices.argento,
            isLoading: false,
            error: null,
            lastFetch: new Date()
          });
        }
      } catch (error) {
        if (mounted) {
          setData(prev => ({
            ...prev,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Errore nel caricamento dei prezzi'
          }));
        }
      }
    };

    fetchOnce();

    return () => {
      mounted = false;
    };
  }, []);

  return data;
}
